import {
  PollAnswer,
  SurveyInitializationResult,
  SurveyPlatform,
  SurveySettings,
} from "../types/common";

import {
  LmsApiErrorResponse,
  LmsSurvey,
  LmsQuestion,
  LmsQuestionType,
  LmsPollAnswer,
  LmsResponseSuccessResult,
} from "../types/smartLms";
import {
  Survey,
  Question,
  SurveyPreview,
  SurveyPreviewResponse,
  BaseQuestion,
  Poll,
  PollStatus,
  VbPollAnswer,
  SurveyStatus,
  QuestionType,
  QuestionsGroup,
  QuestionsGroupOptions,
  QuestionPreview,
} from "../types/valuebuilder";
import lmsApiService from "@/shared/services/lmsApiService";
import vbApiService from "@/shared/services/vbApiService";

class SurveyService {
  /**
   * Initializes a survey with all necessary data and settings
   *
   * @param uid - Survey unique identifier
   * @param platform - Survey platform (Vb, Preview, etc.)
   * @param currentSettings - Existing settings from store
   *
   * @returns Complete survey initialization result
   */
  /**
   * Initializes a survey with all necessary data and settings
   *
   * Main initialization method for live surveys. Handles survey data loading,
   * settings management, and poll generation for production survey sessions.
   * Sets preview mode to false for live survey execution.
   *
   * @param uid - Survey unique identifier
   * @param platform - Survey platform (Vb, Lms)
   * @param currentSettings - Existing settings from store
   *
   * @returns Complete survey initialization result
   *
   * @example
   * ```typescript
   * const result = await service.initializeSurvey('survey-123', 'Vb', settings);
   * // Returns: { survey: {...}, questions: [...], settings: {...} }
   * ```
   */
  async initializeSurvey(
    uid: string,
    platform: SurveyPlatform,
    currentSettings: SurveySettings | null
  ): Promise<SurveyInitializationResult> {
    // Step 1: Load survey data
    const surveyData = await this.getSurveyAndQuestions(uid, platform);

    // Step 2: Handle settings and poll generation
    const settings = await this.handleSurveySettings(
      uid,
      platform,
      false,
      currentSettings
    );

    return {
      survey: surveyData.survey,
      questions: surveyData.questions,
      settings,
    };
  }
  /**
   * Initializes a preview survey with data and settings
   *
   * Specialized initialization method for survey preview mode. Handles preview-specific
   * data loading and settings configuration without requiring live poll generation.
   * Currently only supports ValueBuilder platform previews.
   *
   * @param previewId - Preview session unique identifier
   * @param platform - Survey platform (currently only supports 'Vb')
   * @param currentSettings - Existing settings from store
   *
   * @returns Complete survey initialization result for preview
   *
   * @throws {Error} When LMS platform is requested (not yet implemented)
   *
   * @example
   * ```typescript
   * const result = await service.initializePreviewSurvey('preview-123', 'Vb', null);
   * // Returns: { survey: {...}, questions: [...], settings: {...} }
   * ```
   */
  async initializePreviewSurvey(
    previewId: string,
    platform: SurveyPlatform,
    currentSettings: SurveySettings | null
  ): Promise<SurveyInitializationResult> {
    // Step 1: Load survey data
    const surveyData = await this.getSurveyPreview(previewId, platform);

    // Step 2: Handle settings and poll generation
    const settings = await this.handleSurveySettings(
      previewId,
      platform,
      true,
      currentSettings
    );

    return {
      survey: surveyData.survey,
      questions: surveyData.questions,
      settings,
    };
  }

  /**
   * Validates survey data before initialization
   *
   * @param uid - Survey UID to validate
   * @param platform - Platform to validate
   */
  validateSurveyInitParams(uid: string, platform: SurveyPlatform): void {
    if (!uid || uid.trim().length === 0) {
      throw new Error("Survey UID is required");
    }

    if (!platform) {
      throw new Error("Survey platform is required");
    }

    const validPlatforms: SurveyPlatform[] = ["Lms", "Vb"];
    if (!validPlatforms.includes(platform)) {
      throw new Error(`Invalid platform: ${platform}`);
    }
  }

  /**
   * Handles settings creation and poll generation logic
   *
   * Creates or updates survey settings based on current parameters and handles
   * conditional poll generation for non-preview ValueBuilder surveys.
   * Updates platform and preview mode settings as needed.
   *
   * @private
   * @param uid - Survey unique identifier
   * @param platform - Survey platform type
   * @param isPreview - Whether this is a preview session
   * @param currentSettings - Existing settings from store
   *
   * @returns Updated settings with poll ID if generated, or null
   *
   * @throws {Error} When poll generation fails
   */
  private async handleSurveySettings(
    uid: string,
    platform: SurveyPlatform,
    isPreview: boolean,
    currentSettings: SurveySettings | null
  ): Promise<SurveySettings | null> {
    // Create base settings
    const settings: SurveySettings = currentSettings || {
      surveyId: uid,
      platform,
      isPreview,
    };

    const platformChanged = settings.platform !== platform;
    const invalidateCurrentSettings =
      settings.surveyId !== uid ||
      platformChanged ||
      settings.isPreview !== isPreview;

    if (invalidateCurrentSettings) {
      settings.surveyId = uid;
      settings.platform = platform;
      settings.isPreview = isPreview;
    }

    if (settings.isPreview) {
      delete settings.pollId;
    }

    // Generate poll if business rules require it
    if (this.shouldGeneratePoll(settings, platformChanged)) {
      try {
        const pollResponse = await (settings.platform === "Vb"
          ? this.generateVbPoll(uid)
          : this.generateLmsPoll(uid));

        return {
          ...settings,
          pollId: pollResponse.id,
        };
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error";
        throw new Error(`Poll generation failed: ${errorMessage}`);
      }
    }

    return settings;
  }

  /**
   * Business rule: Determines if poll generation is needed
   *
   * @private
   */
  private shouldGeneratePoll(
    settings: SurveySettings,
    platformChanged: boolean
  ): boolean {
    return (
      (settings.pollId === undefined || platformChanged) && !settings.isPreview
    );
  }

  /**
   * Retrieves survey data and questions based on platform type
   *
   * Acts as a router method that delegates to the appropriate platform-specific
   * method for fetching survey data. Handles both LMS and ValueBuilder platforms.
   *
   * @param id - Survey unique identifier
   * @param type - Platform type (Lms or Vb)
   *
   * @returns Promise resolving to survey and questions data
   *
   * @example
   * ```typescript
   * const { survey, questions } = await service.getSurveyAndQuestions('123', 'Vb');
   * ```
   */
  async getSurveyAndQuestions(
    id: string,
    type: SurveyPlatform
  ): Promise<{ survey: Survey; questions: Question[] }> {
    if (type === "Lms") return this.getLmsSurvey(id);
    const vbSurveyResponse = await this.getVbSurvey(id);

    const questionsResponse = await this.getVbSurveyQuestions(id);

    return {
      survey: vbSurveyResponse,
      questions: questionsResponse,
    };
  }

  /**
   * Retrieves survey data by UID
   *
   * Fetches basic survey information including title, description,
   * configuration, and metadata.
   *
   * @param surveyUid - Unique identifier for the survey (defaults to demo survey)
   *
   * @returns Promise resolving to survey data with success/error status
   */
  private async getVbSurvey(surveyUid = "5c76aa83e44e7"): Promise<Survey> {
    const response = await vbApiService.get<Survey>(`surveys/${surveyUid}`);

    return response.data;
  }

  /**
   * Retrieves survey preview data for a specific platform
   *
   * Fetches survey preview data based on the platform type (LMS or ValueBuilder).
   * Delegates to the appropriate method for each platform.
   *
   * @param surveyUid - Unique identifier for the survey
   * @param platform - Platform type (Lms or Vb)
   *
   * @returns Promise resolving to survey data with success/error status
   */
  private async getSurveyPreview(
    surveyUid: string,
    platform: SurveyPlatform
  ): Promise<{ survey: Survey; questions: Question[] }> {
    if (platform === "Lms") return this.getLmsSurvey(surveyUid, true);
    return this.getVbPreviewSurvey(surveyUid);
  }

  /**
   * Retrieves preview survey data
   *
   * Fetches survey data for preview mode, allowing users to test
   * surveys before they are published.
   *
   * @param previewId - UUID of the preview session
   *
   * @returns Promise resolving to parsed preview survey data
   */
  private async getVbPreviewSurvey(
    previewId: string
  ): Promise<{ survey: Survey; questions: Question[] }> {
    const { data } = await vbApiService.get<SurveyPreviewResponse>(
      `survey_previews/${previewId}`
    );

    try {
      const surveyPreview = JSON.parse(data.surveyData) as SurveyPreview;

      return this.mapVbSurveyPreviewToRegular(surveyPreview);
    } catch (error) {
      console.error("Error parsing preview survey data:", error);
      throw error;
    }
  }

  /**
   * Maps preview survey data to regular survey structure
   *
   * Transforms a SurveyPreview object (containing embedded questions) into separate
   * Survey and Question array structures. Handles the conversion from preview format
   * to the standard application data structure.
   *
   * @private
   * @param survey - Preview survey data with embedded questions
   *
   * @returns Object containing separated survey and questions data
   *
   * @example
   * ```typescript
   * const mapped = service.mapVbSurveyPreviewToRegular(previewData);
   * // Returns: { survey: {...}, questions: [...] }
   * ```
   */
  private mapVbSurveyPreviewToRegular(survey: SurveyPreview): {
    survey: Survey;
    questions: Question[];
  } {
    const { questions, ...surveyData } = survey;

    return {
      survey: surveyData,
      questions: questions.map((q) => this.mapVbQuestionPreviewToRegular(q)),
    };
  }

  /**
   * Maps preview question data to regular question structure
   *
   * Converts a QuestionPreview object to a standard Question object, handling
   * missing fields, ID generation, and recursive child question mapping.
   * Ensures compatibility between preview and regular question structures.
   *
   * @private
   * @param question - Preview question data to convert
   *
   * @returns Standard Question object with all required fields
   *
   * @example
   * ```typescript
   * const regularQ = service.mapVbQuestionPreviewToRegular(previewQuestion);
   * // Returns: Question with generated IDs and mapped children
   * ```
   */
  private mapVbQuestionPreviewToRegular(question: QuestionPreview): Question {
    const regularQuestion: Question = {
      ...question,
      is_displayed: question.is_displayed ?? true,
      question_id: !question.question_id
        ? this.generateRandomId()
        : question.question_id, // Fallback ID generation
      questionsGroup: question.answer,
      questionsGroupOptions: question.answer_variant,
      children: question.children?.map((child) =>
        this.mapVbQuestionPreviewToRegular(child)
      ),
    };

    return regularQuestion;
  }

  /**
   * Generates a random alphanumeric identifier
   *
   * Creates a random ID string for questions that don't have a question_id.
   * Used as a fallback for preview questions that may lack proper identifiers.
   * Generates an 8-character lowercase alphanumeric string.
   *
   * @private
   *
   * @returns Random 8-character string (lowercase letters only)
   *
   * @example
   * ```typescript
   * const id = service.generateRandomId();
   * // Returns: "abcdefgh" (example)
   * ```
   */
  private generateRandomId() {
    return Math.random()
      .toString(36)
      .replace(/[^a-z]+/g, "")
      .substring(2, 10);
  }

  /**
   * Retrieves and formats survey questions
   *
   * Fetches all questions for a survey and organizes them into a hierarchical
   * structure where child questions are nested under their parent questions.
   *
   * @param surveyUid - Unique identifier for the survey
   *
   * @returns Promise resolving to formatted question hierarchy
   *
   * @example
   * ```typescript
   * const result = await API.getSurveyQuestions('survey-123');
   * // Returns questions with children nested:
   * // [
   * //   { question_id: 1, text: "Parent question", children: [
   * //     { question_id: 2, parent_id: 1, text: "Child question" }
   * //   ]}
   * // ]
   * ```
   */
  private async getVbSurveyQuestions(
    surveyUid = "5c76aa83e44e7"
  ): Promise<Question[]> {
    const response = await vbApiService.get<BaseQuestion[]>(
      `surveys/${surveyUid}/questions`
    );
    const questions = response.data;
    const questionsFormatted: Question[] = [];
    try {
      questions.forEach((item) => {
        const itemToAdd: Question = {
          ...item,
          questionsGroup: item.answer,
          questionsGroupOptions: item.answer_variant,
        };
        if (item.parent_id === 0) {
          questionsFormatted.push(itemToAdd);
        } else {
          const parentIndex = questionsFormatted.findIndex(
            (el) => el.question_id === itemToAdd.parent_id
          );
          questionsFormatted[parentIndex]["children"]
            ? questionsFormatted[parentIndex]["children"].push(itemToAdd)
            : (questionsFormatted[parentIndex]["children"] = [itemToAdd]);
        }
      });
      return questionsFormatted;
    } catch (error) {
      console.error("Error formatting questions:", error);
      throw error;
    }
  }

  /**
   * Generates a new poll instance for a survey
   *
   * Creates a new poll session that can be used to collect responses
   * for a specific survey.
   *
   * @param surveyUid - Unique identifier for the survey
   *
   * @returns Promise resolving to poll ID for the new session
   */
  async generateVbPoll(surveyUid: string): Promise<Poll> {
    const response = await vbApiService.post<Poll>(`survey_polls`, {
      survey: `/surveys/${surveyUid}`,
    });

    return response.data;
  }

  /**
   * Updates the status of a poll
   *
   * Changes the poll status (e.g., STARTED, IN_PROGRESS, COMPLETED)
   * to track user progress through the survey.
   *
   * @param pollId - Unique identifier for the poll session
   * @param status - New status to set for the poll
   *
   * @returns Promise resolving to update operation result
   */
  async updatePollStatus(
    settings: SurveySettings,
    status: PollStatus
  ): Promise<boolean> {
    if (!settings.pollId) {
      throw new Error("Missing poll ID");
    }

    if (settings.platform === "Vb") {
      return !!(await this.updateVbPollStatus(settings.pollId, status));
    } else {
      return !!(await this.updateLmsPollStatus(settings.pollId, status));
    }
  }

  /**
   * Updates the status of a VB poll
   *
   * Changes the poll status (e.g., STARTED, IN_PROGRESS, COMPLETED)
   * to track user progress through the survey.
   *
   * @param pollId - Unique identifier for the poll session
   * @param status - New status to set for the poll
   *
   * @returns Promise resolving to update operation result
   */
  private async updateVbPollStatus(
    pollId: number,
    status: PollStatus
  ): Promise<Poll> {
    const response = await vbApiService.patch<Poll>(
      `survey_polls/${pollId}`,

      {
        status: status,
      },
      {
        headers: {
          "Content-Type": "application/merge-patch+json",
        },
      }
    );
    return response.data;
  }

  /**
   * Updates the status of a LMS poll
   *
   * Changes the poll status (e.g., STARTED, IN_PROGRESS, COMPLETED)
   * to track user progress through the survey.
   *
   * @param pollId - Unique identifier for the poll session
   * @param status - New status to set for the poll
   *
   * @returns Promise resolving to update operation result
   */
  private async updateLmsPollStatus(
    pollId: number,
    status: PollStatus
  ): Promise<boolean> {
    const data = new FormData();
    data.append("status", status.toString());
    data.append(
      "wsfunction",
      "mod_satisfactionsurvey_update_public_response_status"
    );
    data.append("responseid", pollId.toString());
    const response = await lmsApiService.post<{ success: boolean }>("", data);

    if (!response.data.success) {
      throw new Error("Failed to update LMS poll status");
    }

    return response.data.success;
  }

  /**
   * Universal answer submission handler
   *
   * Routes poll answer submission to the appropriate platform-specific method
   * based on the survey settings. Handles both ValueBuilder and LMS platforms
   * with proper error handling and validation.
   *
   * @param settings - Survey settings containing platform and poll information
   * @param pollAnswers - Array of poll answers to submit
   *
   * @returns Promise resolving to submission response
   *
   * @throws {Error} When poll ID is missing for VB platform or submission fails
   *
   * @example
   * ```typescript
   * const settings = { platform: 'Vb', pollId: 123, surveyId: 'survey-456' };
   * const answers = [{ value: "5", surveyPollQuestion: "/questions/1" }];
   * await service.submitAnswers(settings, answers);
   * ```
   */
  async submitAnswers(
    settings: SurveySettings,
    pollAnswers: PollAnswer[]
  ): Promise<unknown> {
    if (!settings.pollId) {
      throw new Error("Missing poll ID, please refresh the page.");
    }

    if (settings.platform === "Vb") {
      return this.submitVbPollAnswers(
        settings.pollId,
        pollAnswers as VbPollAnswer[]
      );
    } else {
      return this.submitLmsPollAnswers(
        settings.surveyId,
        settings.pollId,
        pollAnswers as LmsPollAnswer[]
      );
    }
  }

  /**
   * Submits poll answers to ValueBuilder backend
   *
   * Handles the submission of user responses for ValueBuilder platform surveys.
   * Sends poll answers to the VB API for storage and processing.
   *
   * @private
   * @param pollId - Unique identifier for the poll session
   * @param pollAnswers - Array of ValueBuilder poll answers
   *
   * @returns Promise resolving to submission response data
   *
   * @throws {Error} When submission fails or API returns error
   */
  private async submitVbPollAnswers(
    pollId: number | string,
    pollAnswers: VbPollAnswer[]
  ): Promise<unknown> {
    const { data } = await vbApiService.post(
      `/survey_polls/${pollId}/poll_answers`,
      {
        pollAnswers: pollAnswers,
      }
    );
    return data;
  }

  /**
   * Submits poll answers to LMS backend
   *
   * Handles the submission of user responses for LMS platform surveys.
   * Uses FormData to submit answers to the Smart LMS API endpoint.
   *
   * @private
   * @param uid - Survey unique identifier in LMS
   * @param pollAnswers - Array of LMS poll answers
   *
   * @returns Promise resolving to LMS submission response
   *
   * @throws {Error} When LMS returns error code or submission fails
   *
   * @example
   * ```typescript
   * const lmsAnswers = [{ questionId: 1, answer: "5" }];
   * await service.submitLmsPollAnswers('survey-123', lmsAnswers);
   * ```
   */
  private async submitLmsPollAnswers(
    uid: string,
    pollId: number,
    pollAnswers: LmsPollAnswer[]
  ): Promise<unknown> {
    const data = new FormData();
    data.append("uid", uid);
    data.append("responseid", pollId.toString());
    data.append("wsfunction", "mod_satisfactionsurvey_submit_public_response");
    data.append("response_data", JSON.stringify(pollAnswers));

    const response = await lmsApiService.post<
      LmsApiErrorResponse | LmsResponseSuccessResult
    >("", data);
    if (response.data && "errorcode" in response.data) {
      throw new Error(response.data.errorcode);
    }
    if (!response.data?.success) throw new Error("Failed to submit answers.");

    return response.data;
  }

  /**
   * Retrieves LMS survey data and questions
   *
   * Fetches survey information from the Smart LMS platform using FormData
   * to communicate with the LMS web service. Maps LMS data structures to
   * the application's internal Survey and Question types.
   *
   * @private
   * @param surveyId - LMS survey unique identifier
   *
   * @returns Promise resolving to mapped survey and questions data
   *
   * @throws {Error} When LMS returns error code or data mapping fails
   *
   * @example
   * ```typescript
   * const { survey, questions } = await service.getLmsSurvey('lms-survey-123');
   * ```
   */
  private async getLmsSurvey(
    surveyId: string,
    isPreview: boolean = false
  ): Promise<{ survey: Survey; questions: Question[] }> {
    const data = new FormData();
    data.append("uid", surveyId);
    data.append("is_preview", isPreview ? "1" : "0");
    data.append("wsfunction", "mod_satisfactionsurvey_get_public_survey");

    const response = await lmsApiService.post<LmsApiErrorResponse | LmsSurvey>(
      "",
      data
    );
    if (response.data && "errorcode" in response.data) {
      throw new Error(response.data.errorcode);
    }

    const mappedSurvey = this.mapLmsSurveyToSurvey(response.data);
    const mappedQuestions = response.data.questions.map((question) =>
      this.mapLmsQuestionToQuestion(question)
    );

    return {
      survey: mappedSurvey,
      questions: mappedQuestions,
    };
  }

  private async generateLmsPoll(uid: string): Promise<{ id: number }> {
    const data = new FormData();
    data.append("uid", uid);
    data.append(
      "wsfunction",
      "mod_satisfactionsurvey_generate_public_response"
    );

    const response = await lmsApiService.post<{
      success: boolean;
      responseid: number;
      message: string;
    }>("", data);

    if (response.data.success) {
      return { id: response.data.responseid };
    }

    throw new Error(response.data.message);
  }

  /**
   * Maps LMS survey data to internal Survey type
   *
   * Transforms LMS survey structure to match the application's Survey interface.
   * Handles field mapping, type conversion, and default value assignment.
   *
   * @private
   * @param lmsSurvey - Raw LMS survey data
   *
   * @returns Mapped Survey object compatible with application types
   *
   * @example
   * ```typescript
   * const survey = service.mapLmsSurveyToSurvey(lmsData);
   * // Returns: { id: 123, title: "Survey Title", status: "ACTIVE", ... }
   * ```
   */
  private mapLmsSurveyToSurvey(lmsSurvey: LmsSurvey): Survey {
    return {
      id: lmsSurvey.id,
      title: lmsSurvey.name,
      description: lmsSurvey.description,
      confirmation: lmsSurvey.settings.confirmation_text,
      siteUrl: lmsSurvey.settings.website_link,
      siteLinkText: lmsSurvey.settings.website_link_text,
      companyName: undefined,
      uniqid: lmsSurvey.public_uid,
      status: lmsSurvey.active ? SurveyStatus.ACTIVE : SurveyStatus.CLOSED,
      stepType:
        lmsSurvey.type === "CUSTOMER" ? "customer_score" : "hub_and_spoke",
      dateAdded: undefined,
      dateModified: undefined,
    };
  }

  /**
   * Maps LMS question data to internal Question type
   *
   * Transforms LMS question structure to match the application's Question interface.
   * Handles complex question types, radio groups, and hierarchical question relationships.
   * Supports recursive mapping for child questions and special handling for radio groups.
   *
   * @private
   * @param lmsQuestion - Raw LMS question data
   * @param position - Question position in the survey (default: 0)
   * @param parentId - Parent question ID for nested questions (default: 0)
   *
   * @returns Mapped Question object with proper type conversion and children
   *
   * @example
   * ```typescript
   * const question = service.mapLmsQuestionToQuestion(lmsQ, 1, 0);
   * // Returns: { id: 123, label: "Question text", type: "radio", children: [...] }
   * ```
   */
  private mapLmsQuestionToQuestion(
    lmsQuestion: LmsQuestion,
    position = 0,
    parentId: string | number = 0
  ): Question {
    const type = this.mapLmsQuestionTypeToVbType(
      lmsQuestion.type,
      lmsQuestion.subQuestions
    );
    return {
      id: lmsQuestion.id,
      question_id: lmsQuestion.id,
      label: lmsQuestion.text,
      label_max: lmsQuestion.settings?.maxLabel ?? null,
      label_min: lmsQuestion.settings?.minLabel ?? null,
      type,
      max_value: lmsQuestion.settings?.max ?? 0,
      survey_id: null,
      position: position,
      parent_id: parentId,
      ...this.setupRadioGroupFromChildren(
        type,
        lmsQuestion.subQuestions ?? [],
        lmsQuestion.options
      ),
      is_required: lmsQuestion.required,
      is_displayed: lmsQuestion.visible,
      is_displayed_editable: false, // this is not being used
      children:
        type !== "radiogroup"
          ? lmsQuestion.subQuestions?.map((child) =>
              this.mapLmsQuestionToQuestion(child, position + 1, lmsQuestion.id)
            )
          : undefined,
    };
  }

  /**
   * Sets up radio group configuration from child questions
   *
   * Creates radio group structure by extracting options and questions from
   * LMS sub-questions. Builds questionsGroup and questionsGroupOptions objects
   * required for radio group rendering in the UI.
   *
   * @private
   * @param type - Question type to determine if radio group setup is needed
   * @param questions - Array of child questions to process
   *
   * @returns Object containing questionsGroup and questionsGroupOptions or null values
   *
   * @example
   * ```typescript
   * const setup = service.setupRadioGroupFromChildren('radiogroup', childQuestions);
   * // Returns: {
   * //   questionsGroup: { "1": "Question 1", "2": "Question 2" },
   * //   questionsGroupOptions: { "0": "Option A", "1": "Option B" }
   * // }
   * ```
   */
  private setupRadioGroupFromChildren(
    type: QuestionType,
    questions: LmsQuestion[],
    options?: string[]
  ): {
    questionsGroup: QuestionsGroup | null;
    questionsGroupOptions: QuestionsGroupOptions | null;
  } {
    if (!questions.length || type !== "radiogroup" || !options)
      return { questionsGroup: null, questionsGroupOptions: null };

    const questionsGroup: Record<string, string> = questions.reduce<
      Record<string, string>
    >((acc, question) => {
      acc[`${question.id}`] = question.text;
      return acc;
    }, {});

    const questionsGroupOptions: Record<string, string> = options.reduce<
      Record<string, string>
    >((acc, option, index) => {
      acc[`${index}`] = option;
      return acc;
    }, {});

    return { questionsGroup, questionsGroupOptions };
  }

  /**
   * Maps LMS question types to ValueBuilder question types
   *
   * Converts LMS-specific question types to the application's internal QuestionType enum.
   * Handles special logic for determining radio groups based on child question types.
   *
   * @private
   * @param lmsType - LMS question type identifier
   * @param children - Optional array of child questions for type determination
   *
   * @returns Mapped QuestionType compatible with the application
   *
   * @throws {Error} When encountering unknown LMS question type
   *
   * @example
   * ```typescript
   * const type = service.mapLmsQuestionTypeToVbType('scale', null);
   * // Returns: "radio"
   *
   * const groupType = service.mapLmsQuestionTypeToVbType('none', childQuestions);
   * // Returns: "radiogroup" if children contain multiplechoice
   * ```
   */
  private mapLmsQuestionTypeToVbType(
    lmsType: LmsQuestionType,
    children?: LmsQuestion[] | null
  ): QuestionType {
    switch (lmsType) {
      case "scale":
        return "radio";
      case "text":
      case "textarea":
        return "textarea";
      case "radiogroup":
        if (children && children.length > 0) {
          return "radiogroup";
        }
        throw new Error(`Radio group needs children`);

      default:
        throw new Error(`Unknown LMS question type: ${lmsType}`);
    }
  }
}

export default new SurveyService();
